import { useState } from 'react';
import { Link } from 'react-router-dom';

const WHATSAPP_NUM = '971547172715';
const NEWSLETTER_URL = 'https://readdy.ai/api/form/d727g3c991ove783tcf0';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch(NEWSLETTER_URL, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const quickLinks = [
    { label: 'الصفحة الرئيسية', path: '/' },
    { label: 'المنتجات', path: '/products' },
    { label: 'أفلام ومسلسلات', path: '/movies' },
    { label: 'الأسئلة الشائعة', path: '/faq' },
    { label: 'تواصل معنا', path: '/contact' },
    { label: 'سياسة الخصوصية', path: '/faq' },
  ];

  const socials = [
    { icon: 'ri-instagram-line', label: 'إنستغرام', href: '#' },
    { icon: 'ri-facebook-fill', label: 'فيسبوك', href: '#' },
    { icon: 'ri-twitter-x-line', label: 'تويتر', href: '#' },
    { icon: 'ri-tiktok-fill', label: 'تيك توك', href: 'https://www.tiktok.com/@dubai002z' },
    { icon: 'ri-snapchat-fill', label: 'سناب شات', href: '#' },
  ];

  return (
    <footer className="relative bg-black text-white pt-20 pb-0 overflow-hidden" dir="rtl">
      {/* Top cinematic line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E8721A]/60 to-transparent" />

      {/* Decorative bg */}
      <div className="absolute inset-0 opacity-[0.02] select-none pointer-events-none overflow-hidden">
        <div className="absolute top-10 right-10 text-[300px] font-black text-[#E8721A] leading-none">ع</div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">

        {/* Newsletter Banner */}
        <div className="border border-[#E8721A]/20 bg-[#E8721A]/5 px-8 py-8 mb-16 flex flex-col lg:flex-row items-center justify-between gap-6">
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-[#E8721A]/40 hidden lg:block" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-[#E8721A]/40 hidden lg:block" />

          <div className="text-right">
            <h3 className="text-white font-black text-xl mb-1 tracking-wide">اشترك في نشرتنا البريدية</h3>
            <p className="text-white/30 text-xs tracking-widest">احصل على أحدث العروض والمجموعات الحصرية مباشرة في بريدك</p>
          </div>
          <form
            onSubmit={handleNewsletter}
            data-readdy-form
            className="flex flex-row-reverse w-full lg:w-auto min-w-0 lg:min-w-[380px] bg-white/5 border border-white/10 overflow-hidden"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="بريدك الإلكتروني"
              className="flex-1 bg-transparent px-5 py-3 text-sm text-white placeholder-white/20 outline-none text-right"
            />
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#E8721A] text-white px-6 py-3 text-xs font-bold whitespace-nowrap cursor-pointer hover:bg-[#C05A0A] transition-colors tracking-widest uppercase"
            >
              {submitted ? '✓ تم' : submitting ? '...' : 'اشترك'}
            </button>
          </form>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 pb-14 border-b border-white/5">

          {/* Brand */}
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/196e3761-b52b-4536-9200-ac860c5863e1.png"
              alt="شعار العالمية الدولية"
              className="h-14 w-auto object-contain mb-5"
            />
            <p className="text-white/30 text-sm leading-relaxed mb-6">
              متجرك الأول للعطور والأزياء الفاخرة. نقدم أجود المنتجات بأسعار تنافسية مع خدمة توصيل سريعة لجميع دول الخليج.
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-3 mb-6">
              {socials.map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  rel="nofollow noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 flex items-center justify-center border border-white/10 text-white/30 hover:text-[#E8721A] hover:border-[#E8721A]/40 transition-all duration-300 cursor-pointer"
                >
                  <i className={`${s.icon} text-sm`} />
                </a>
              ))}
            </div>

            {/* App Download Badges */}
            <div>
              <p className="text-white/20 text-xs mb-3 tracking-widest uppercase">حمّل تطبيقنا</p>
              <div className="flex flex-col gap-2.5">
                <a
                  href="#"
                  className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#E8721A]/30 px-4 py-2.5 transition-all duration-300 cursor-pointer group w-fit"
                >
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-apple-fill text-white text-xl" />
                  </div>
                  <div>
                    <div className="text-white/30 text-[9px] font-medium leading-none mb-0.5 whitespace-nowrap">Download on the</div>
                    <div className="text-white font-black text-sm leading-none whitespace-nowrap">App Store</div>
                  </div>
                </a>
                <a
                  href="#"
                  className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#E8721A]/30 px-4 py-2.5 transition-all duration-300 cursor-pointer group w-fit"
                >
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-google-play-fill text-white text-lg" />
                  </div>
                  <div>
                    <div className="text-white/30 text-[9px] font-medium leading-none mb-0.5 whitespace-nowrap">GET IT ON</div>
                    <div className="text-white font-black text-sm leading-none whitespace-nowrap">Google Play</div>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 id="footer-links" className="text-white font-bold text-sm mb-6 flex items-center gap-2 tracking-widest uppercase">
              <div className="w-4 h-px bg-[#E8721A]" />
              <a href="#footer-links">روابط سريعة</a>
            </h4>
            <ul className="space-y-3">
              {quickLinks.map(link => (
                <li key={link.path + link.label}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-white/30 text-sm hover:text-[#E8721A] transition-colors cursor-pointer group"
                  >
                    <div className="w-3 h-3 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-arrow-left-s-line text-xs text-[#E8721A]" />
                    </div>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 id="footer-contact" className="text-white font-bold text-sm mb-6 flex items-center gap-2 tracking-widest uppercase">
              <div className="w-4 h-px bg-[#E8721A]" />
              <a href="#footer-contact">تواصل معنا</a>
            </h4>
            <ul className="space-y-5">
              <li>
                <a
                  href={`https://wa.me/${WHATSAPP_NUM}`}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-3 group cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center border border-white/10 group-hover:border-[#25D366]/40 transition-colors duration-300">
                    <i className="ri-whatsapp-line text-white/30 group-hover:text-[#25D366] text-lg transition-colors" />
                  </div>
                  <div className="text-right">
                    <div className="text-white/20 text-xs mb-0.5 tracking-widest uppercase">واتساب</div>
                    <span className="text-white/60 text-sm font-bold" dir="ltr">+{WHATSAPP_NUM}</span>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center border border-white/10">
                    <i className="ri-mail-line text-white/30 text-lg" />
                  </div>
                  <div className="text-right">
                    <div className="text-white/20 text-xs mb-0.5 tracking-widest uppercase">البريد الإلكتروني</div>
                    <span className="text-white/40 text-sm">aliix009x@gmail.com</span>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center border border-white/10">
                    <i className="ri-map-pin-line text-white/30 text-lg" />
                  </div>
                  <div className="text-right">
                    <div className="text-white/20 text-xs mb-0.5 tracking-widest uppercase">العنوان</div>
                    <span className="text-white/40 text-sm">دبي، الإمارات العربية المتحدة</span>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Payment Methods Strip */}
        <div className="py-6 border-b border-white/5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-white/20">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-lock-2-line text-sm text-[#E8721A]" />
              </div>
              <span className="text-xs font-bold tracking-widest uppercase">طرق الدفع المقبولة — دفع آمن 100%</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <div className="bg-white rounded px-3 py-1.5 flex items-center justify-center h-8 min-w-[48px]">
                <span className="text-[#1A1F71] font-black text-sm italic tracking-widest leading-none">VISA</span>
              </div>
              <div className="bg-white rounded px-2.5 py-1.5 flex items-center gap-1.5 h-8">
                <div className="flex items-center relative">
                  <div className="w-4 h-4 rounded-full bg-[#EB001B]" />
                  <div className="w-4 h-4 rounded-full bg-[#F79E1B] -mr-2 opacity-90" />
                </div>
                <span className="text-[10px] font-black text-gray-600 mr-1 leading-none">MC</span>
              </div>
              <div className="bg-black border border-white/10 rounded px-3 py-1.5 flex items-center gap-1.5 h-8">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-apple-fill text-white text-base" />
                </div>
                <span className="text-white font-bold text-xs leading-none">Pay</span>
              </div>
              <div className="bg-[#3DBDA7] rounded px-3 py-1.5 flex items-center gap-1.5 h-8">
                <span className="text-white font-black text-sm leading-none">tabby</span>
                <span className="bg-white/25 text-white text-[10px] font-black px-1.5 py-0.5 rounded leading-none">4×</span>
              </div>
              <div className="bg-white rounded px-3 py-1.5 flex items-center justify-center h-8 min-w-[48px] border border-gray-100">
                <span className="text-[#00A651] font-black text-sm leading-none">mada</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-5">
          <p className="text-white/20 text-xs tracking-widest">
            &copy; {new Date().getFullYear()} جميع الحقوق محفوظة &mdash; العالمية الدولية
          </p>
          <div className="flex items-center gap-3 text-white/20 text-xs tracking-widest">
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-shield-keyhole-line text-[#E8721A] text-xs" />
              </div>
              SSL مشفر
            </span>
            <span className="text-white/10">|</span>
            <span>سياسة الخصوصية</span>
            <span className="text-white/10">|</span>
            <span>الشروط والأحكام</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
